import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
    FaBolt,
    FaTools,
    FaShieldAlt,
    FaWrench,
    FaCogs,
    FaMapMarkerAlt,
    FaCheckCircle,
    FaArrowRight,
    FaClock,
    FaHome,
    FaTint,
    FaLightbulb,
    FaFire,
    FaPhoneAlt,
    FaCalendarAlt,
    FaStar,
    FaHardHat,
    FaIndustry,
    FaWater,
    FaPlug,
    FaMicrochip,
    FaUserTie,
    FaClipboardCheck,
    FaHeartbeat,
    FaGlobe,
    FaAward,
    FaRocket,
    FaHandshake
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const Services = () => {
    const navigate = useNavigate();

    const [activeService, setActiveService] = useState(0);
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                duration: 0.6
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const services = [
        {
            id: 1,
            title: "Electrical Solutions",
            shortTitle: "Electrical",
            subtitle: "Complete electrical solutions from planning to execution",
            icon: FaBolt,
            gradient: "from-blue-500 to-indigo-600",
            features: [
                {
                    title: "Electrical Material Mapping",
                    description: "Comprehensive planning and documentation process where all required electrical materials are identified, listed, and allocated based on project design and site layout.",
                    icon: FaMapMarkerAlt,
                    benefits: [
                        "Complete material identification and listing",
                        "Strategic site layout planning and optimization",
                        "Right materials at right places and time",
                        "Cost-effective resource allocation and budgeting"
                    ]
                },
                {
                    title: "Professional Installation Services",
                    description: "Expert installation of electrical materials at specified locations, following approved electrical drawings, international safety standards, and industry best practices.",
                    icon: FaTools,
                    benefits: [
                        "Installation per approved technical drawings",
                        "Strict adherence to international safety standards",
                        "Comprehensive quality assurance and testing",
                        "Best practices implementation and certification"
                    ]
                }
            ],
            stats: [
                { number: "500+", label: "Projects" },
                { number: "99.9%", label: "Safety" },
                { number: "20+", label: "Years" }
            ]
        },
        {
            id: 2,
            title: "Building Maintenance",
            shortTitle: "Maintenance",
            subtitle: "Comprehensive building care for optimal performance",
            icon: FaHome,
            gradient: "from-green-500 to-emerald-600",
            features: [
                {
                    title: "Waterproofing Solutions",
                    description: "Advanced waterproofing services for comprehensive building protection including roofing systems, basements, bathrooms, and external wall treatments.",
                    icon: FaTint,
                    benefits: [
                        "Advanced roofing waterproofing systems",
                        "Basement moisture protection solutions",
                        "Professional bathroom sealing services",
                        "External wall weatherproofing treatments"
                    ]
                },
                {
                    title: "Routine Maintenance Programs",
                    description: "Systematic daily, weekly, and monthly maintenance programs designed to keep buildings clean, safe, and fully functional at all times.",
                    icon: FaCogs,
                    benefits: [
                        "Comprehensive floor and common area maintenance",
                        "Lighting systems maintenance and replacement",
                        "Mechanical parts lubrication and servicing",
                        "Preventive maintenance scheduling and monitoring"
                    ]
                }
            ],
            stats: [
                { number: "20+", label: "Years" },
                { number: "1000+", label: "Buildings" },
                { number: "1hr", label: "Response" }
            ]
        },
        {
            id: 3,
            title: "Industrial Safety Solutions",
            shortTitle: "Safety",
            subtitle: "Advanced safety systems for hazardous environments",
            icon: FaHardHat,
            gradient: "from-orange-500 to-red-600",
            features: [
                {
                    title: "Explosion-Proof Equipment",
                    description: "Specialized explosion-proof electrical equipment and systems designed for hazardous industrial environments with flammable gases, vapors, or dust.",
                    icon: FaFire,
                    benefits: [
                        "ATEX and IECEx certified equipment supply",
                        "Hazardous area classification analysis",
                        "Explosion-proof lighting and control systems",
                        "Safety compliance documentation and certification"
                    ]
                },
                {
                    title: "Safety System Integration",
                    description: "Comprehensive safety system design and integration including fire detection, gas monitoring, and safety instrumented systems.",
                    icon: FaShieldAlt,
                    benefits: [
                        "Advanced fire detection and suppression systems",
                        "Real-time gas leak detection and monitoring",
                        "Safety instrumented systems (SIS) design",
                        "Working hours technical support"
                    ]
                }
            ],
            stats: [
                { number: "200+", label: "Projects" },
                { number: "100%", label: "Compliance" },
                { number: "Zero", label: "Incidents" }
            ]
        }
    ];

    const whyChooseUsItems = [
        {
            icon: FaAward,
            title: "Industry Excellence",
            description: "ISO certified processes with international quality standards and comprehensive warranties",
            color: "text-blue-500"
        },
        {
            icon: FaRocket,
            title: "Innovation Driven",
            description: "Cutting-edge technology solutions with continuous innovation and improvement",
            color: "text-green-500"
        },
        {
            icon: FaClock,
            title: "Timely Execution",
            description: "On-schedule project completion with efficient resource management and delivery",
            color: "text-orange-500"
        },
        {
            icon: FaWrench,
            title: "Expert Team",
            description: "Skilled professionals with extensive industry experience and technical expertise",
            color: "text-purple-500"
        },
        {
            icon: FaGlobe,
            title: "Global Standards",
            description: "International compliance with global safety and quality standards",
            color: "text-indigo-500"
        },
        {
            icon: FaHandshake,
            title: "Professional Support",
            description: "Dedicated support during working hours: Saturday-Thursday 8AM-1PM, 4PM-8:30PM",
            color: "text-red-500"
        }
    ];

    const goToContact = () => navigate('/contact');

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">

            {/* Enhanced Hero Section */}
            <motion.section
                className="relative pt-20 pb-16 md:pt-28 md:pb-24 lg:pt-32 lg:pb-32 bg-gradient-to-br from-primary via-primary/95 to-primary/90 overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            >
                {/* Enhanced Background Pattern */}
                <div className="absolute inset-0">
                    <motion.div
                        className="absolute top-10 md:top-20 left-5 md:left-10 w-48 md:w-96 h-48 md:h-96 bg-secondary/20 rounded-full blur-3xl"
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.3, 0.7, 0.3],
                            rotate: [0, 180, 360]
                        }}
                        transition={{
                            duration: 12,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                    <motion.div
                        className="absolute bottom-10 md:bottom-20 right-10 md:right-20 w-36 md:w-72 h-36 md:h-72 bg-blue-400/20 rounded-full blur-3xl"
                        animate={{
                            scale: [1.2, 1, 1.2],
                            opacity: [0.2, 0.6, 0.2],
                            rotate: [360, 180, 0]
                        }}
                        transition={{
                            duration: 15,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 2
                        }}
                    />
                    <motion.div
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 md:w-48 h-24 md:h-48 bg-indigo-400/15 rounded-full blur-2xl"
                        animate={{
                            scale: [1, 1.4, 1],
                            opacity: [0.4, 0.8, 0.4],
                        }}
                        transition={{
                            duration: 10,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 4
                        }}
                    />
                </div>

                <div className="container mx-auto px-4 md:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-5xl mx-auto"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-2 md:gap-3 bg-secondary/90 backdrop-blur-sm px-4 md:px-8 py-2 md:py-4 rounded-full mb-6 md:mb-10 shadow-xl border border-secondary/20"
                        >
                            <FaStar className="text-primary text-sm md:text-lg" />
                            <span className="text-primary font-bold text-xs md:text-base uppercase tracking-wider">
                                Professional Industrial Services
                            </span>
                            <FaStar className="text-primary text-sm md:text-lg" />
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 md:mb-8 leading-tight"
                        >
                            Expert Industrial
                            <span className="block text-secondary drop-shadow-2xl">Solutions</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed font-medium"
                        >
                            From electrical installations to comprehensive building maintenance and industrial safety solutions,
                            we deliver world-class services that ensure safety, efficiency, and reliability for your critical projects.
                        </motion.p>

                        {/* Stats Preview - Updated */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mt-8 md:mt-12 max-w-4xl mx-auto"
                        >
                            {[
                                { number: "20+", label: "Years Experience" },
                                { number: "2000+", label: "Projects Delivered" },
                                { number: "6 Days", label: "Support Available" },
                                { number: "99.9%", label: "Success Rate" }
                            ].map((stat, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 + 0.8 }}
                                    className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-3 md:p-6 border border-white/20"
                                >
                                    <div className="text-xl md:text-3xl font-bold text-secondary mb-1 md:mb-2">{stat.number}</div>
                                    <div className="text-gray-200 font-medium text-xs md:text-sm">{stat.label}</div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Enhanced Services Section */}
            <motion.section
                ref={ref}
                className="py-12 md:py-20 lg:py-28"
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <div className="container mx-auto px-4 md:px-8">
                    {/* Enhanced Service Selector */}
                    <motion.div
                        variants={itemVariants}
                        className="flex justify-center mb-12 md:mb-20"
                    >
                        <div className="flex flex-wrap justify-center gap-2 md:gap-3 bg-white/80 backdrop-blur-lg rounded-2xl md:rounded-3xl p-2 md:p-3 shadow-2xl border border-gray-200/50 max-w-full overflow-x-auto">
                            {services.map((service, index) => (
                                <motion.button
                                    key={service.id}
                                    onClick={() => setActiveService(index)}
                                    whileHover={{ scale: 1.02, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`flex items-center gap-2 md:gap-3 px-3 md:px-6 py-2 md:py-4 rounded-xl md:rounded-2xl font-semibold transition-all duration-500 relative overflow-hidden text-sm md:text-base ${activeService === index
                                        ? 'bg-gradient-to-r ' + service.gradient + ' text-white shadow-xl transform scale-105'
                                        : 'text-gray-600 hover:bg-gray-50 hover:shadow-md'
                                        }`}
                                >
                                    {activeService === index && (
                                        <motion.div
                                            layoutId="activeBackground"
                                            className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                                            transition={{ duration: 0.3 }}
                                        />
                                    )}
                                    <service.icon className="text-base md:text-xl relative z-10" />
                                    <span className="relative z-10 whitespace-nowrap">{service.shortTitle}</span>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>

                    {/* Enhanced Active Service Content - Content Only (Image Removed) */}
                    <div className="max-w-4xl mx-auto mb-16 md:mb-24">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`content-${activeService}`}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                            >
                                {/* Header */}
                                <div className="flex items-start gap-4 mb-8">
                                    <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${services[activeService].gradient} rounded-2xl md:rounded-3xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                                        {React.createElement(services[activeService].icon, {
                                            className: "text-2xl md:text-3xl text-white"
                                        })}
                                    </div>
                                    <div className="min-w-0">
                                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 leading-tight">
                                            {services[activeService].title}
                                        </h2>
                                        <p className="text-primary font-semibold text-base md:text-lg">
                                            {services[activeService].subtitle}
                                        </p>
                                    </div>
                                </div>

                                {/* Stats Row */}
                                <div className="grid grid-cols-3 gap-4 mb-8">
                                    {services[activeService].stats.map((stat, index) => (
                                        <motion.div
                                            key={`stat-${activeService}-${index}`}
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: index * 0.1 + 0.2 }}
                                            className="bg-white rounded-2xl p-4 text-center shadow-lg border border-gray-100"
                                        >
                                            <div className="text-xl md:text-2xl font-bold text-primary mb-1">
                                                {stat.number}
                                            </div>
                                            <div className="text-xs md:text-sm text-gray-600 font-medium">
                                                {stat.label}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Service Features */}
                                <div className="space-y-6">
                                    {services[activeService].features.map((feature, index) => (
                                        <motion.div
                                            key={`feature-${activeService}-${index}`}
                                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            transition={{ delay: index * 0.2 + 0.4, type: "spring", stiffness: 100 }}
                                            className="bg-white/90 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100/50 hover:shadow-2xl transition-all duration-300"
                                        >
                                            <div className="flex items-start gap-4 mb-6">
                                                <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br ${services[activeService].gradient} rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                                                    <feature.icon className="text-lg md:text-xl text-white" />
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                                                        {feature.title}
                                                    </h3>
                                                    <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                                                        {feature.description}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Benefits List */}
                                            <div className="grid grid-cols-1 gap-3">
                                                {feature.benefits.map((benefit, idx) => (
                                                    <motion.div
                                                        key={`benefit-${activeService}-${index}-${idx}`}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: idx * 0.1 + 0.6 }}
                                                        className="flex items-center gap-3 bg-gray-50/80 rounded-lg p-3 hover:bg-gray-100/80 transition-colors duration-200"
                                                    >
                                                        <FaCheckCircle className="text-secondary text-sm md:text-base flex-shrink-0" />
                                                        <span className="text-gray-700 font-medium text-sm md:text-base">{benefit}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* CTA Button */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.8, type: "spring", stiffness: 100 }}
                                    className="mt-8 text-center"
                                >
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className={`w-full sm:w-auto bg-gradient-to-r ${services[activeService].gradient} text-white px-8 py-4 rounded-xl font-bold text-base md:text-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-2xl mx-auto`}
                                        onClick={goToContact}
                                    >
                                        <FaPhoneAlt className="text-sm md:text-base" />
                                        Get Free Consultation
                                        <FaArrowRight className="text-sm md:text-base" />
                                    </motion.button>
                                </motion.div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Why Choose Us Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 rounded-2xl md:rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden"
                    >
                        <div className="absolute inset-0 opacity-10">
                            <motion.div
                                className="absolute top-5 md:top-10 right-5 md:right-10 w-32 md:w-64 h-32 md:h-64 bg-secondary rounded-full blur-2xl"
                                animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                                transition={{ duration: 20, repeat: Infinity }}
                            />
                            <motion.div
                                className="absolute bottom-5 md:bottom-10 left-5 md:left-10 w-24 md:w-48 h-24 md:h-48 bg-blue-400 rounded-full blur-2xl"
                                animate={{ rotate: -360, scale: [1.2, 1, 1.2] }}
                                transition={{ duration: 25, repeat: Infinity }}
                            />
                        </div>

                        <div className="relative z-10">
                            <div className="text-center mb-12">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                                    Why Choose Our Services?
                                </h2>
                                <p className="text-gray-200 text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                                    Professional expertise backed by decades of experience, cutting-edge technology,
                                    and unwavering commitment to excellence
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {whyChooseUsItems.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                                        animate={inView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.9 }}
                                        transition={{ delay: index * 0.15 + 0.8, type: "spring", stiffness: 100 }}
                                        className="text-center bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                                    >
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                            <item.icon className={`text-2xl md:text-3xl ${item.color}`} />
                                        </div>
                                        <h3 className="text-lg md:text-xl font-bold mb-3">{item.title}</h3>
                                        <p className="text-gray-200 leading-relaxed text-sm md:text-base">{item.description}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Business Hours Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.8, delay: 1.0 }}
                        className="mt-16 bg-gradient-to-r from-secondary/10 to-secondary/5 rounded-2xl md:rounded-3xl p-8 border border-secondary/20"
                    >
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-3 mb-6">
                                <FaClock className="text-primary text-2xl" />
                                <h3 className="text-2xl md:text-3xl font-bold text-primary">Business Hours</h3>
                            </div>
                            <div className="max-w-2xl mx-auto">
                                <p className="text-gray-700 mb-6 text-lg">
                                    Our professional team is available to serve you during our business hours
                                </p>
                                <div className="grid md:grid-cols-2 gap-6 text-center">
                                    <div className="bg-white/70 rounded-xl p-6">
                                        <p className="font-bold text-primary mb-2 text-lg">Saturday - Thursday</p>
                                        <p className="text-gray-600 text-base">8:00 AM - 1:00 PM</p>
                                        <p className="text-gray-600 text-base">4:00 PM - 8:30 PM</p>
                                    </div>
                                    <div className="bg-white/70 rounded-xl p-6">
                                        <p className="font-bold text-primary mb-2 text-lg">Friday</p>
                                        <p className="text-gray-600 text-base">Closed</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact CTA Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                        className="text-center mt-16 bg-white/90 backdrop-blur-lg rounded-2xl md:rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-100/50"
                    >
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                            Ready to Transform Your Project?
                        </h2>
                        <p className="text-gray-600 text-base sm:text-lg md:text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
                            Contact our expert team today for a comprehensive consultation and discover how our
                            customized solutions can exceed your project expectations.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-gradient-to-r from-primary to-primary/90 text-white px-8 py-4 rounded-xl font-bold text-base md:text-lg transition-all duration-300 flex items-center gap-3 justify-center shadow-2xl"
                                onClick={goToContact}
                            >
                                <FaPhoneAlt />
                                Contact Us
                                <FaArrowRight />
                            </motion.button>

                        </div>

                        <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-gray-600">
                            <div className="flex items-center gap-2">
                                <FaPhoneAlt className="text-primary" />
                                <span className="font-semibold">+966 57 018 5488</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaHandshake className="text-primary" />
                                <span className="font-semibold">Free Consultation Available</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.section>

        </div>
    );
};

export default Services;